import type { ParentToIframe, IframeToParent } from './types.js';

/**
 * postMessage protocol — cal-core embed widget
 *
 * Parent → iframe  (sent right after iframe loads)
 *   { type: 'cal:init', theme?: string, prefill?: { email?, name? }, locale?: string }
 *
 * Iframe → parent  (sent by the booking landing page)
 *   { type: 'cal:resize', height: number }    — request parent to grow/shrink iframe
 *   { type: 'cal:booked', bookingId: string } — booking completed successfully
 *   { type: 'cal:close' }                     — user cancelled / wants to close modal
 *
 * Security: all messages are validated against the expected cal origin before acting.
 */

export type { ParentToIframe, IframeToParent };

/** Send an init message to an iframe immediately after it loads. */
export function sendInit(
  iframeEl: HTMLIFrameElement,
  calOrigin: string,
  msg: Omit<ParentToIframe, 'type'> & { type?: 'cal:init' },
): void {
  const payload: ParentToIframe = { type: 'cal:init', ...msg };
  // Use '*' fallback if origin is ambiguous (localhost dev), but prefer explicit.
  const targetOrigin = calOrigin || '*';
  iframeEl.contentWindow?.postMessage(payload, targetOrigin);
}

/**
 * Register a listener for messages coming from the embedded landing page.
 * Returns an unsubscribe function.
 */
export function onIframeMessage(
  calOrigin: string,
  handler: (msg: IframeToParent) => void,
): () => void {
  function listener(event: MessageEvent): void {
    // Origin guard — only trust messages from the cal server.
    // In dev (localhost) calOrigin may be "http://localhost:3000"; allow it.
    if (calOrigin && event.origin !== calOrigin) return;

    const data = event.data as unknown;
    if (!isIframeToParent(data)) return;
    handler(data);
  }

  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

function isIframeToParent(data: unknown): data is IframeToParent {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d['type'] === 'cal:resize' ||
    d['type'] === 'cal:booked' ||
    d['type'] === 'cal:close'
  );
}
